import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SettingsIcon from '@mui/icons-material/Settings';
import { getDay, isValid, parseISO } from 'date-fns';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import PageHeader from '../../../sharedModules/layout/PageHeader';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import {
  CollectionsCorporateLocation,
  getCollectionsSelectedLocation,
} from '../../navigation/components/CollectionsLocationToggle';
import { collectionCandidatesQueryKey } from '../../clients/hooks/useCollectionClients';
import { useCollectionConfig, useUpdateCollectionConfig } from '../hooks/useCollectionConfig';
import {
  CollectionConfig,
  CollectionEmailTemplate,
  CollectionFrequency,
  FREQUENCIES,
  FREQUENCY_LABELS,
  MONTH_DAYS,
  SCHEDULE_DAYS,
  TEMPLATE_VARIABLES,
  TONE_LABELS,
} from '../model';
import { TONE_COLOR_BY_KEY } from '../../shared/toneColors';
import { residentialToggleImpactText } from '../residentialToggleLabel';

const TONES: CollectionEmailTemplate['tone'][] = ['friendly', 'second_notice', 'urgent'];

const QUILL_MODULES = {
  toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']],
};

// SCHEDULE_DAYS is Monday-anchored; date-fns getDay returns 0 = Sunday.
const scheduleDayFromJs = (jsDay: number): string => SCHEDULE_DAYS[(jsDay + 6) % 7];

const weekdayMatchesAnchor = (scheduleDay: string | null, anchorDate: string | null): boolean => {
  if (!scheduleDay || !anchorDate) return true;
  const parsed = parseISO(anchorDate);
  if (!isValid(parsed)) return true;
  return scheduleDayFromJs(getDay(parsed)) === scheduleDay;
};

const normalizeConfig = (raw: CollectionConfig): CollectionConfig => ({
  ...raw,
  include_residential: raw.include_residential ?? false,
  residential_client_count: raw.residential_client_count ?? 0,
});

const buildSavePayload = (config: CollectionConfig) => ({
  config: {
    sender_display_name: config.sender_display_name,
    reply_to_email: config.reply_to_email,
    friendly_threshold_days: config.friendly_threshold_days,
    second_notice_threshold_days: config.second_notice_threshold_days,
    include_residential: config.include_residential,
    email_templates_attributes: config.email_templates,
  },
});

const defaultsForFrequency = (
  frequency: CollectionFrequency,
  current: CollectionEmailTemplate,
): Pick<CollectionEmailTemplate, 'schedule_day' | 'schedule_anchor_date' | 'schedule_day_of_month'> => {
  switch (frequency) {
    case 'weekly':
      return {
        schedule_day: current.schedule_day ?? 'Monday',
        schedule_anchor_date: null,
        schedule_day_of_month: null,
      };
    case 'biweekly':
      return {
        schedule_day: current.schedule_day ?? 'Monday',
        schedule_anchor_date: current.schedule_anchor_date,
        schedule_day_of_month: null,
      };
    case 'monthly':
      return {
        schedule_day: null,
        schedule_anchor_date: null,
        schedule_day_of_month: current.schedule_day_of_month ?? 1,
      };
  }
};

interface LocationTabProps {
  location: CollectionsCorporateLocation;
  enabled: boolean;
}

const LocationTab: React.FC<LocationTabProps> = ({ location: locationKey, enabled }) => {
  const configUrl = `/collections/configs/${locationKey}`;
  const { data, isLoading, isError } = useCollectionConfig(locationKey, enabled);
  const { mutateAsync, isPending } = useUpdateCollectionConfig();
  const queryClient = useQueryClient();
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);

  const [config, setConfig] = useState<CollectionConfig | null>(null);
  const [activeToneTab, setActiveToneTab] = useState(0);

  useEffect(() => {
    setConfig(null);
  }, [locationKey]);

  useEffect(() => {
    const result = data?.data;
    if (result) setConfig(normalizeConfig(result));
  }, [data?.data, locationKey]);

  const persistConfig = useCallback(
    async (nextConfig: CollectionConfig) => {
      const response = await mutateAsync({
        url: configUrl,
        data: buildSavePayload(nextConfig),
      });
      const saved = response.data ? normalizeConfig(response.data) : nextConfig;
      setConfig(saved);
      await queryClient.invalidateQueries({ queryKey: [configUrl] });
      return saved;
    },
    [configUrl, mutateAsync, queryClient],
  );

  if (isLoading) return <Box sx={{ p: 4, textAlign: 'center' }}>Loading...</Box>;
  if (isError) return <ErrorMessage message="Failed to load configuration." />;
  if (!config) return null;

  const activeTemplate =
    config.email_templates.find((t) => t.tone === TONES[activeToneTab]) ?? config.email_templates[0];

  const updateField = (field: keyof CollectionConfig, value: unknown) =>
    setConfig((prev) => (prev ? { ...prev, [field]: value } : prev));

  const updateTemplate = (
    tone: CollectionEmailTemplate['tone'],
    field: keyof CollectionEmailTemplate,
    value: string | number | null,
  ) =>
    setConfig((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        email_templates: prev.email_templates.map((t) => (t.tone === tone ? { ...t, [field]: value } : t)),
      };
    });

  const updateFrequency = (tone: CollectionEmailTemplate['tone'], frequency: CollectionFrequency) =>
    setConfig((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        email_templates: prev.email_templates.map((t) => {
          if (t.tone !== tone) return t;
          return { ...t, frequency, ...defaultsForFrequency(frequency, t) };
        }),
      };
    });

  const handleCopyVariable = async (variable: string) => {
    try {
      await navigator.clipboard.writeText(variable);
      showSuccessSnackBar?.(`Copied ${variable}`);
    } catch {
      showErrorSnackBar?.('Failed to copy to clipboard.');
    }
  };

  const handleSave = async () => {
    try {
      await persistConfig(config);
      showSuccessSnackBar?.('Configuration saved.');
    } catch {
      showErrorSnackBar?.('Failed to save configuration.');
    }
  };

  const handleIncludeResidentialChange = async (checked: boolean) => {
    const previous = config.include_residential;
    const nextConfig = { ...config, include_residential: checked };
    setConfig(nextConfig);
    try {
      await persistConfig(nextConfig);
      void queryClient.invalidateQueries({
        queryKey: collectionCandidatesQueryKey(locationKey),
      });
      showSuccessSnackBar?.('Client eligibility updated.');
    } catch {
      setConfig({ ...config, include_residential: previous });
      showErrorSnackBar?.('Failed to update client eligibility.');
    }
  };

  return (
    <LoadingWrapper isLoading={isPending} message="Saving...">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Client Eligibility */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Client Eligibility
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            When off, only commercial clients will receive scheduled emails. Residential clients remain visible in the
            Clients list but cannot be activated.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={config.include_residential}
                onChange={(e) => handleIncludeResidentialChange(e.target.checked)}
                color="primary"
                disabled={isPending}
              />
            }
            label={
              <Box>
                <Typography component="span">Include residential clients in scheduled emails</Typography>
                <Typography variant="body2" color="textSecondary" display="block">
                  {residentialToggleImpactText(config.residential_client_count, config.include_residential)}
                </Typography>
              </Box>
            }
          />
        </Paper>

        {/* Sender Settings */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Sender Settings
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sender Display Name"
                fullWidth
                value={config.sender_display_name}
                onChange={(e) => updateField('sender_display_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Reply-To Email"
                type="email"
                fullWidth
                value={config.reply_to_email}
                onChange={(e) => updateField('reply_to_email', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Tone Thresholds */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Tone Thresholds
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Aging days determine which email tone a client receives. Urgent is anything above the Second Notice
            threshold.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Friendly — up to X days"
                type="number"
                fullWidth
                value={config.friendly_threshold_days}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value, 10);
                  if (!Number.isNaN(parsed)) updateField('friendly_threshold_days', parsed);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Second Notice — up to X days"
                type="number"
                fullWidth
                value={config.second_notice_threshold_days}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value, 10);
                  if (!Number.isNaN(parsed)) updateField('second_notice_threshold_days', parsed);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Urgent — above Second Notice threshold"
                fullWidth
                disabled
                value={`> ${config.second_notice_threshold_days} days`}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Email Templates */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Email Templates
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Tabs value={activeToneTab} onChange={(_, v) => setActiveToneTab(v)} sx={{ mb: 2 }}>
            {TONES.map((tone) => (
              <Tab
                key={tone}
                label={TONE_LABELS[tone]}
                sx={{ color: TONE_COLOR_BY_KEY[tone], '&.Mui-selected': { color: TONE_COLOR_BY_KEY[tone] } }}
              />
            ))}
          </Tabs>

          {!activeTemplate && (
            <Typography color="textSecondary" sx={{ mt: 2 }}>
              No template configured for this tone.
            </Typography>
          )}
          {activeTemplate && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={4}>
                  <TextField
                    select
                    label="Frequency"
                    fullWidth
                    value={activeTemplate.frequency}
                    onChange={(e) => updateFrequency(activeTemplate.tone, e.target.value as CollectionFrequency)}
                  >
                    {FREQUENCIES.map((freq) => (
                      <MenuItem key={freq} value={freq}>
                        {FREQUENCY_LABELS[freq]}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {activeTemplate.frequency !== 'monthly' && (
                  <Grid item xs={6} sm={4}>
                    <TextField
                      select
                      label="Schedule Day"
                      fullWidth
                      value={activeTemplate.schedule_day ?? ''}
                      onChange={(e) => updateTemplate(activeTemplate.tone, 'schedule_day', e.target.value)}
                    >
                      {SCHEDULE_DAYS.map((day) => (
                        <MenuItem key={day} value={day}>
                          {day}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}

                {activeTemplate.frequency === 'monthly' && (
                  <Grid item xs={6} sm={4}>
                    <TextField
                      select
                      label="Day of Month"
                      fullWidth
                      value={activeTemplate.schedule_day_of_month ?? 1}
                      onChange={(e) =>
                        updateTemplate(activeTemplate.tone, 'schedule_day_of_month', parseInt(e.target.value, 10))
                      }
                      helperText="Months with fewer days will send on the last day."
                    >
                      {MONTH_DAYS.map((d) => (
                        <MenuItem key={d} value={d}>
                          {d}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}

                <Grid item xs={6} sm={4}>
                  <TextField
                    label="Schedule Time (HH:MM)"
                    fullWidth
                    value={activeTemplate.schedule_time}
                    onChange={(e) => updateTemplate(activeTemplate.tone, 'schedule_time', e.target.value)}
                    placeholder="09:00"
                  />
                </Grid>

                {activeTemplate.frequency === 'biweekly' && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Anchor Date (first send)"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={activeTemplate.schedule_anchor_date ?? ''}
                      onChange={(e) => updateTemplate(activeTemplate.tone, 'schedule_anchor_date', e.target.value)}
                    />
                    {!weekdayMatchesAnchor(activeTemplate.schedule_day, activeTemplate.schedule_anchor_date) && (
                      <FormHelperText error>
                        Anchor date weekday must match Schedule Day ({activeTemplate.schedule_day}).
                      </FormHelperText>
                    )}
                  </Grid>
                )}
              </Grid>

              <TextField
                label="Email Subject"
                fullWidth
                value={activeTemplate.subject}
                onChange={(e) => updateTemplate(activeTemplate.tone, 'subject', e.target.value)}
                sx={{ mb: 2 }}
              />

              <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Template Variables — click to copy, then paste into the subject or body
                </Typography>
                {TEMPLATE_VARIABLES.map(({ variable, description }) => (
                  <Tooltip key={variable} title={description}>
                    <Chip
                      icon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                      label={variable}
                      onClick={() => handleCopyVariable(variable)}
                      clickable
                      variant="outlined"
                      size="small"
                      sx={{ mr: 1, mb: 0.5 }}
                    />
                  </Tooltip>
                ))}
              </Box>

              <Typography variant="body2" color="textSecondary" gutterBottom>
                Email Body
              </Typography>
              {/* height sets the container; marginBottom prevents the Quill toolbar from overlapping elements below */}
              <ReactQuill
                placeholder="Enter email body here..."
                theme="snow"
                value={activeTemplate.body}
                onChange={(value) => updateTemplate(activeTemplate.tone, 'body', value)}
                modules={QUILL_MODULES}
                style={{ height: 200, marginBottom: 48 }}
              />

              {(activeTemplate.body.match(/\{\{invoice_table\}\}/g) || []).length > 1 && (
                <FormHelperText error sx={{ mb: 1 }}>
                  Only the first {'{{invoice_table}}'} will render the invoice table. Subsequent markers will appear as
                  literal text in the email.
                </FormHelperText>
              )}

              <Box sx={{ mt: 2, p: 2, bgcolor: '#fafafa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Invoice Table Preview — rendered in the email wherever{' '}
                  <Box component="span" sx={{ fontFamily: 'monospace' }}>
                    {'{{invoice_table}}'}
                  </Box>{' '}
                  appears in the body above. The list of invoices is generated at send time from the customer&apos;s
                  past-due aging records.
                </Typography>
                <Table size="small" sx={{ bgcolor: '#ffffff' }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#1e3a5f' }}>
                      {['Invoice #', 'Invoice Date', 'Due Date', 'Age Beyond Terms'].map((label) => (
                        <TableCell key={label} sx={{ color: '#fff', fontWeight: 'bold' }}>
                          {label}
                        </TableCell>
                      ))}
                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }} align="right">
                        Balance Due
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>159015</TableCell>
                      <TableCell>02/10/2026</TableCell>
                      <TableCell>03/12/2026</TableCell>
                      <TableCell>49 days</TableCell>
                      <TableCell align="right">$340.00</TableCell>
                    </TableRow>
                    <TableRow sx={{ borderTop: '2px solid #1e3a5f' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 'bold' }}>
                        Total Past Due
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        $340.00
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}
        </Paper>

        <Box>
          <Button variant="contained" color="primary" onClick={handleSave}>
            Save Configuration
          </Button>
        </Box>
      </Box>
    </LoadingWrapper>
  );
};

const CollectionConfigPage: React.FC = () => {
  const [location] = useState(() => getCollectionsSelectedLocation());

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle="Collections — Configuration" titleIcon={<SettingsIcon />} />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <LocationTab location={location} enabled />
      </Grid>
    </Grid>
  );
};

export default CollectionConfigPage;
